import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { AuthService } from 'utility/services/auth'
import { UsersService } from 'utility/services/user'
import { SubscriptionService } from 'utility/services/subscription'
import {
    loadStripe,
    paymentFormatNote,
    publishableKeyFromPublicKey,
    resolveStripePublishableKey,
} from 'utility/stripe-loader'
import {
    ANNUAL_SAVINGS_PER_SEAT_CENTS,
    MAX_SEATS,
    MIN_SEATS,
    formatMoney,
    formatPerPeriod,
    normalizeSeatCount,
    pickPremiumPlan,
    planListFromResponse,
    asCartOrder,
    quoteLicense,
    readStripeKey,
    serverPricingIsValid,
} from 'utility/license-pricing'
import { ACCOUNT_PLAN_PATH } from 'utility/subscription-usage'
import { useSubscriptionUsage } from 'context/subscription-usage'

const PENDING_KEY = 'eauditor.licenseCheckout'
const DASHBOARD_PATH = '/app/templates'
const TERMS_URL = 'https://eauditor.app/'
const PRIVACY_URL = 'https://eauditor.app/'

const readPendingCheckout = () => {
    try {
        const raw = sessionStorage.getItem(PENDING_KEY)
        return raw ? JSON.parse(raw) : null
    } catch {
        return null
    }
}

const rememberCheckout = payload => {
    try {
        sessionStorage.setItem(PENDING_KEY, JSON.stringify(payload))
    } catch {
        // Redirect resume is best-effort when storage is blocked.
    }
}

const clearPendingCheckout = () => {
    try {
        sessionStorage.removeItem(PENDING_KEY)
    } catch {
        // Ignore storage failures.
    }
}

const messageFrom = err => {
    const apiErr = err?.error
    const fromList =
        apiErr?.Errors?.[0]?.ErrorMessage ||
        apiErr?.errors?.[0]?.ErrorMessage ||
        apiErr?.errors?.[0]?.errorMessage
    return (
        fromList ||
        apiErr?.ErrorMessage ||
        apiErr?.errorMessage ||
        apiErr?.message ||
        apiErr?.Message ||
        err?.message ||
        'Something went wrong. Please try again.'
    )
}

const accountEmail = () => {
    const user = AuthService.getUser() || {}
    const candidates = [user.Email, user.EmailAddress, user.UserName]
    return (
        candidates.find(
            value => typeof value === 'string' && value.includes('@'),
        ) || ''
    )
}

const accountCompanyId = () => {
    const user = AuthService.getUser() || {}
    return user.CompanyId || user.Companies?.[0]?.CompanyId || null
}

const billingApiValue = frequency =>
    frequency === 'annual' ? 'Annual' : 'Monthly'

const dollarsFromCents = cents => Math.round(Number(cents)) / 100

const quoteFromPriceChange = err => {
    const body = err?.error
    if (!body || typeof body !== 'object') return null
    const code = body.ErrorCode || body.errorCode
    if (code !== 'PriceChanged') return null
    return asCartOrder(body.Quote || body.quote || body)
}

const formatLongDate = value => {
    const date = value instanceof Date ? value : new Date(value)
    if (Number.isNaN(date.getTime())) return ''
    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    })
}

const nextRenewalDate = (frequency, order) => {
    const subscription = order?.Subscription || order?.subscription
    const fromServer =
        order?.RenewalDate ||
        order?.renewalDate ||
        order?.NextBillingDate ||
        order?.nextBillingDate ||
        subscription?.RenewalDate ||
        subscription?.renewalDate ||
        subscription?.NextBillingDate ||
        order?.PlanEndingOn ||
        order?.planEndingOn
    if (fromServer) {
        const parsed = new Date(fromServer)
        if (!Number.isNaN(parsed.getTime())) return parsed
    }
    const date = new Date()
    if (frequency === 'annual') date.setFullYear(date.getFullYear() + 1)
    else date.setMonth(date.getMonth() + 1)
    return date
}

const orderNumberOf = order => {
    const id =
        order?.CartOrderId ??
        order?.cartOrderId ??
        order?.OrderNo ??
        order?.orderNo ??
        order?.OrderNumber ??
        order?.orderNumber
    if (id == null || id === '') return ''
    const text = String(id)
    return text.startsWith('#') ? text : `#${text}`
}

const emailWasSent = payload => {
    if (!payload || typeof payload !== 'object') return true
    const nested = payload.Data || payload.data || payload
    const flag =
        nested?.ReceiptEmailSent ??
        nested?.receiptEmailSent ??
        payload.ReceiptEmailSent ??
        payload.receiptEmailSent
    if (flag === false || flag === 0 || flag === 'false') return false
    return true
}

const recordSuccessfulPayment = (order, intent, companyId) => {
    const chargeId =
        (typeof intent?.latest_charge === 'string' && intent.latest_charge) ||
        intent?.charges?.data?.[0]?.id ||
        order?.ChargeId ||
        ''
    const methodId =
        (typeof intent?.payment_method === 'string' && intent.payment_method) ||
        order?.PaymentMethodId ||
        ''
    return SubscriptionService.payNow({
        PaymentOrderId: order?.PaymentOrderId || intent?.id || '',
        ChargeId: chargeId,
        TransactionId: intent?.id || order?.TransactionId || '',
        CreatedOn: new Date().toISOString(),
        ResponseMessage: 'Payment succeeded',
        DisplayMessage: 'Payment succeeded',
        ChargeAmount: order?.ChargeAmount ?? order?.TotalDueToday ?? 0,
        status: 'SUCCEEDED',
        OrderNo: order?.OrderNo ?? order?.CartOrderId ?? 0,
        ClientSecretId: order?.ClientSecretId || '',
        MerchantDisplayName: order?.MerchantDisplayName || 'eAuditor',
        PaymentMethodId: methodId,
        ThirdPartyClientId: order?.ThirdPartyClientId || '',
        EphemeralKey: order?.EphemeralKey || '',
        EphemeralKeySecret: order?.EphemeralKeySecret || '',
        CompanyId: companyId || accountCompanyId() || order?.CompanyId || 0,
    }).catch(() => null)
}

const confirmLicenseOrder = order => {
    const cartOrderId = Number(order?.CartOrderId ?? order?.cartOrderId)
    const paymentOrderId = order?.PaymentOrderId ?? order?.paymentOrderId
    if (!Number.isInteger(cartOrderId)) {
        throw new Error(
            'Billing did not return a cart order, so Premium was not activated.',
        )
    }
    if (paymentOrderId == null || paymentOrderId === '') {
        throw new Error(
            'Billing did not return a payment id, so Premium was not activated.',
        )
    }
    return SubscriptionService.confirmCart({ cartOrderId, paymentOrderId })
}

const withServerOrder = async order => {
    const id = Number(order?.CartOrderId ?? order?.cartOrderId)
    if (!Number.isInteger(id)) return order
    try {
        const fresh = asCartOrder(await SubscriptionService.getCartOrder(id))
        if (!fresh || typeof fresh !== 'object') return order
        return {
            ...order,
            ...fresh,
            CartOrderId: order?.CartOrderId ?? fresh.CartOrderId ?? id,
            PaymentOrderId: order?.PaymentOrderId || fresh.PaymentOrderId,
            ClientSecretId: order?.ClientSecretId || fresh.ClientSecretId,
        }
    } catch {
        return order
    }
}

const reviewRows = quote => {
    const rows = [
        ['Team members', String(quote.seats)],
        ['Billing frequency', quote.billingLabel],
    ]
    if (quote.frequency === 'annual') {
        rows.push([
            'Equivalent monthly price',
            `${formatMoney(quote.equivalentMonthlyCents)}/user/month`,
        ])
        rows.push(['Annual price per user', formatMoney(quote.unitCents)])
        rows.push(['Subtotal', formatPerPeriod(quote.totalCents, 'annual')])
    } else {
        rows.push(['Price per user', `${formatMoney(quote.unitCents)} / month`])
        rows.push(['Subtotal', formatPerPeriod(quote.totalCents, 'monthly')])
    }
    rows.push(['Tax', 'Calculated at checkout'])
    return rows
}

const Page = styled.div`
    width: 100%;
    max-width: 440px;
    margin: 0 auto;
    padding: 4px 0 28px;
    color: #111827;
    &.desk {
        @media (min-width: 1080px) {
            max-width: 960px;
        }
    }
`

const Crumb = styled.p`
    font-size: 13px;
    color: #6b7280;
    margin: 0 0 14px;
    strong {
        color: #111827;
        font-weight: 600;
    }
`

const Progress = styled.ol`
    list-style: none;
    display: flex;
    align-items: center;
    gap: 0;
    margin: 0 0 16px;
    padding: 0;
    li {
        display: flex;
        align-items: center;
        flex: 1;
        min-width: 0;
    }
    li:not(:last-child)::after {
        content: '';
        height: 1px;
        flex: 1;
        background: #e5e7eb;
        margin: 0 8px;
    }
    button {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        border: none;
        background: transparent;
        padding: 0;
        cursor: default;
        white-space: nowrap;
    }
    button.can {
        cursor: pointer;
    }
    .num {
        width: 22px;
        height: 22px;
        border-radius: 50%;
        background: #e5e7eb;
        color: #4b5563;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-size: 12px;
        font-weight: 700;
    }
    button.current .num,
    button.done .num {
        background: #263ba6;
        color: #fff;
    }
    .label {
        font-size: 12px;
        font-weight: 600;
        color: #6b7280;
    }
    button.current .label {
        color: #111827;
        font-weight: 700;
    }
    @media (max-width: 930px) {
        .label {
            display: none;
        }
        button.current .label {
            display: inline;
        }
    }
`

const Card = styled.section`
    background: #fff;
    border: 1px solid #e6e8ee;
    border-radius: 16px;
    padding: 14px 14px 12px;
    box-shadow: 0 4px 12px rgba(15, 23, 42, 0.04);
    @media (max-width: 930px) {
        padding: 12px;
        border-radius: 14px;
    }
`

const StepHead = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    font-weight: 700;
    color: #1f2937;
    margin-bottom: 14px;
    .num {
        width: 26px;
        height: 26px;
        border-radius: 50%;
        background: #263ba6;
        color: #fff;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-size: 13px;
    }
`

const Title = styled.h1`
    font-size: 16px;
    line-height: 1.3;
    margin: 0 0 4px;
    @media (max-width: 930px) {
        font-size: 15px;
    }
`

const Sub = styled.p`
    margin: 0 0 16px;
    color: #6b7280;
    font-size: 14px;
`

const FieldLabel = styled.div`
    font-size: 13px;
    font-weight: 700;
    margin-bottom: 8px;
`

const Hint = styled.p`
    margin: 8px 0 16px;
    color: #6b7280;
    font-size: 13px;
`

const Stepper = styled.div`
    display: inline-flex;
    align-items: center;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    overflow: hidden;
    background: #fff;
    button {
        width: 36px;
        height: 36px;
        border: none;
        background: #f8fafc;
        font-size: 18px;
        cursor: pointer;
    }
    button:disabled {
        color: #cbd5e1;
        cursor: default;
    }
    input {
        width: 44px;
        height: 36px;
        border: none;
        border-left: 1px solid #e5e7eb;
        border-right: 1px solid #e5e7eb;
        text-align: center;
        font-size: 15px;
        font-weight: 700;
        color: #111827;
    }
`

const SeatRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 4px;
    ${FieldLabel} {
        margin-bottom: 0;
    }
`

const PlanStage = styled.div`
    display: grid;
    gap: 14px;
    @media (min-width: 1080px) {
        grid-template-columns: minmax(0, 1.25fr) minmax(280px, 0.75fr);
        align-items: start;
    }
`

const SideCart = styled.section`
    display: none;
    @media (min-width: 1080px) {
        display: block;
        background: #fff;
        border: 1px solid #e6e8ee;
        border-radius: 16px;
        padding: 16px;
        box-shadow: 0 4px 12px rgba(15, 23, 42, 0.04);
    }
    h2 {
        margin: 0 0 4px;
        font-size: 16px;
    }
    .lead {
        margin: 0 0 12px;
        color: #6b7280;
        font-size: 13px;
    }
`

const FreqGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: 10px;
    @media (min-width: 1080px) {
        grid-template-columns: 1fr 1fr;
    }
`

const FreqCard = styled.button`
    text-align: left;
    border: 1px solid #e5e7eb;
    background: #fff;
    border-radius: 12px;
    padding: 14px;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    &.on {
        border-color: #263ba6;
        background: #f4f8ff;
        box-shadow: 0 0 0 1px #263ba6;
    }
    .top {
        display: flex;
        gap: 8px;
        align-items: center;
    }
    .dot {
        width: 16px;
        height: 16px;
        border-radius: 50%;
        border: 2px solid #cbd5e1;
        flex: 0 0 auto;
    }
    &.on .dot {
        border-color: #263ba6;
        box-shadow: inset 0 0 0 4px #263ba6;
    }
    .name {
        font-weight: 700;
        font-size: 13px;
        flex: 1;
    }
    .price {
        font-size: 22px;
        font-weight: 800;
        margin-top: 10px;
        color: #111827;
    }
    .per,
    .fine {
        color: #6b7280;
        font-size: 12px;
        margin-top: 2px;
    }
    .badge {
        margin-left: auto;
        background: #dcfce7;
        color: #166534;
        border-radius: 999px;
        padding: 3px 8px;
        font-size: 11px;
        font-weight: 700;
        white-space: nowrap;
    }
`

const Note = styled.div`
    margin-top: 14px;
    border-radius: 12px;
    padding: 12px;
    font-size: 13px;
    line-height: 1.45;
    &.warn {
        background: #fff8eb;
        border: 1px solid #fde68a;
        color: #92400e;
    }
    &.save {
        background: #f0fdf4;
        border: 1px solid #bbf7d0;
        color: #166534;
    }
    &.annual {
        background: #eff6ff;
        border: 1px solid #bfdbfe;
        color: #1e3a8a;
    }
    strong {
        display: block;
        margin-bottom: 2px;
    }
`

const MemberBox = styled.div`
    margin-top: 14px;
    border: 1px solid #e7e5e4;
    border-radius: 12px;
    background: #fff;
    padding: 12px;

    strong {
        display: block;
        font-size: 13px;
        color: #1c1917;
    }

    .lead {
        margin: 4px 0 0;
        color: #78716c;
        font-size: 12px;
        line-height: 1.4;
    }

    .err {
        color: #b91c1c;
        font-size: 12px;
        margin: 8px 0 0;
    }
`

const MemberList = styled.div`
    max-height: 220px;
    overflow: auto;
    margin-top: 8px;
`

const MemberRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 8px 0;
    border-top: 1px solid #f5f5f4;

    .who {
        min-width: 0;
    }

    .name,
    .mail {
        display: block;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .name {
        font-size: 13px;
        font-weight: 600;
        color: #1c1917;
    }

    .mail {
        font-size: 12px;
        color: #78716c;
    }
`

const QuietButton = styled.button`
    flex: none;
    height: 32px;
    padding: 0 10px;
    border-radius: 8px;
    border: 1px solid #e7e5e4;
    background: #fff;
    color: #44403c;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    white-space: nowrap;

    &:disabled {
        opacity: 0.55;
        cursor: default;
    }

    &:hover:not(:disabled) {
        background: #fafaf9;
    }
`

const PrimaryButton = styled.button`
    appearance: none;
    border: none;
    background: #263ba6;
    color: #fff;
    font-weight: 700;
    font-size: 13px;
    border-radius: 10px;
    min-height: 42px;
    padding: 8px 14px;
    cursor: pointer;
    width: 100%;
    &:disabled {
        opacity: 0.55;
        cursor: default;
    }
    &:hover:not(:disabled) {
        background: #1d2f86;
    }
`

const GhostButton = styled.button`
    appearance: none;
    border: none;
    background: transparent;
    color: #4b5563;
    font-weight: 700;
    min-height: 34px;
    padding: 6px 8px;
    cursor: pointer;
    &:disabled {
        opacity: 0.5;
        cursor: default;
    }
`

const SecondaryButton = styled.button`
    appearance: none;
    width: 100%;
    margin-top: 8px;
    border: 1px solid #e5e7eb;
    background: #fff;
    color: #111827;
    font-weight: 700;
    border-radius: 10px;
    min-height: 34px;
    cursor: pointer;
`

const ActionRow = styled.div`
    display: flex;
    gap: 8px;
    align-items: center;
    margin-top: 18px;
    ${PrimaryButton} {
        flex: 1;
    }
    @media (max-width: 930px) {
        flex-direction: column-reverse;
    }
`

const Product = styled.div`
    display: flex;
    gap: 12px;
    align-items: flex-start;
    padding-bottom: 12px;
    margin-bottom: 8px;
    border-bottom: 1px solid #f3f4f6;
    .mark {
        width: 40px;
        height: 40px;
        border-radius: 12px;
        background: #fff7e6;
        display: flex;
        align-items: center;
        justify-content: center;
        flex: 0 0 auto;
    }
    h2 {
        margin: 0;
        font-size: 16px;
    }
    p {
        margin: 2px 0 0;
        color: #6b7280;
        font-size: 13px;
    }
`

const Rows = styled.div`
    .line {
        display: flex;
        justify-content: space-between;
        gap: 12px;
        padding: 9px 0;
        border-bottom: 1px solid #f3f4f6;
        font-size: 14px;
    }
    .line span:first-child {
        color: #6b7280;
    }
    .line strong {
        font-weight: 600;
        text-align: right;
    }
    .total {
        display: flex;
        justify-content: space-between;
        gap: 12px;
        padding-top: 8px;
        font-size: 15px;
        font-weight: 800;
        color: #263ba6;
    }
`

const ErrorBox = styled.div`
    margin-top: 12px;
    padding: 10px 12px;
    border-radius: 10px;
    background: #fef2f2;
    border: 1px solid #fecaca;
    color: #991b1b;
    font-size: 13px;
    line-height: 1.45;
`

const Secure = styled.div`
    display: flex;
    gap: 10px;
    align-items: flex-start;
    margin-bottom: 14px;
    h2 {
        margin: 0;
        font-size: 16px;
    }
    p {
        margin: 2px 0 0;
        color: #6b7280;
        font-size: 13px;
    }
`

const PayHost = styled.div`
    min-height: 160px;
    margin: 8px 0 12px;
`

const PayStage = styled.div`
    @media (min-width: 1080px) {
        display: grid;
        grid-template-columns: minmax(0, 0.92fr) minmax(300px, 1.08fr);
        gap: 20px;
        align-items: start;
    }
`

const CheckRow = styled.label`
    display: flex;
    gap: 8px;
    align-items: flex-start;
    font-size: 13px;
    margin: 8px 0;
    input {
        margin-top: 2px;
    }
`

const Legal = styled.p`
    font-size: 12px;
    color: #6b7280;
    margin: 0 0 4px;
    a {
        color: #263ba6;
    }
`

const Prep = styled.div`
    text-align: center;
    padding: 28px 8px;
    .spin {
        width: 32px;
        height: 32px;
        margin: 14px auto 0;
        border: 3px solid #e5e7eb;
        border-top-color: #263ba6;
        border-radius: 50%;
        animation: license-spin 0.8s linear infinite;
    }
    @keyframes license-spin {
        to {
            transform: rotate(360deg);
        }
    }
`

const Success = styled.div`
    text-align: center;
    .mark {
        width: 52px;
        height: 52px;
        margin: 8px auto 12px;
        border-radius: 50%;
        background: #22c55e;
        display: flex;
        align-items: center;
        justify-content: center;
    }
    h1 {
        margin: 0 0 6px;
        font-size: 16px;
    }
    .lead {
        color: #6b7280;
        margin: 0 0 16px;
    }
`

const STEPS = [
    { id: 1, label: 'Plan' },
    { id: 2, label: 'Cart' },
    { id: 3, label: 'Payment' },
    { id: 4, label: 'Confirm' },
]

const Crown = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
        <path
            fill="#e8a317"
            d="M3 17.5 4.4 9l4.2 3.4L12 6l3.4 6.4L19.6 9 21 17.5H3zm1.2 1.5h15.6V21H4.2v-2z"
        />
    </svg>
)

const Lock = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
        <path
            fill="#263ba6"
            d="M7 10V8a5 5 0 0 1 10 0v2h1a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h1zm2 0h6V8a3 3 0 0 0-6 0v2z"
        />
    </svg>
)

const Check = () => (
    <svg width="36" height="36" viewBox="0 0 24 24" aria-hidden="true">
        <path
            fill="#fff"
            d="M9.2 16.6 4.8 12.2l1.4-1.4 3 3 8-8 1.4 1.4-9.4 9.4z"
        />
    </svg>
)

const AnnualNotice = ({ quote }) => {
    if (quote.frequency !== 'annual') return null
    return (
        <Note className="annual">
            <strong>Charged in full today</strong>
            The entire annual amount of{' '}
            {formatPerPeriod(quote.totalCents, 'annual')} is charged at
            checkout. It is not split into monthly payments.
        </Note>
    )
}

const CartPayment = ({
    stripeKey,
    companyId,
    quote,
    saveCard,
    onSaveCardChange,
    onBack,
    onPaid,
}) => {
    const [phase, setPhase] = useState('prep')
    const [error, setError] = useState('')
    const [order, setOrder] = useState(null)
    const [verdict, setVerdict] = useState(null)
    const [paying, setPaying] = useState(false)
    const [paidIntent, setPaidIntent] = useState(null)
    const [attempt, setAttempt] = useState(0)
    const [formatNote, setFormatNote] = useState('')
    const stripeRef = useRef(null)
    const elementsRef = useRef(null)
    const publishableFromApi = useRef('')
    const saveRef = useRef(saveCard)
    saveRef.current = saveCard
    const { seats, frequency, totalCents, billingLabel, unitCents } = quote

    useEffect(() => {
        let cancelled = false
        const start = async () => {
            setPhase('prep')
            setError('')
            setPaidIntent(null)
            setOrder(null)
            setVerdict(null)
            try {
                const clientId = companyId || accountCompanyId()
                try {
                    if (clientId != null && clientId !== '') {
                        const publicKey = await SubscriptionService.getPublicKey({
                            clientId,
                            source: 'website',
                        })
                        if (!cancelled) {
                            publishableFromApi.current =
                                publishableKeyFromPublicKey(publicKey)
                        }
                    }
                } catch {
                    publishableFromApi.current = ''
                }
                try {
                    const format = await SubscriptionService.getPaymentFormat()
                    if (!cancelled) setFormatNote(paymentFormatNote(format))
                } catch {
                    if (!cancelled) setFormatNote('')
                }
                const expectedTotal = totalCents / 100
                const billingFrequency = billingApiValue(frequency)
                const quoted = asCartOrder(
                    await SubscriptionService.quoteCart({
                        seats,
                        billingFrequency,
                        expectedTotal,
                    }),
                )
                if (cancelled) return
                const quoteVerdict = serverPricingIsValid(quoted, totalCents)
                if (!quoteVerdict.ok) {
                    setOrder(quoted)
                    setVerdict(quoteVerdict)
                    setPhase('mismatch')
                    return
                }
                const quotedDue = dollarsFromCents(quoteVerdict.total)
                let created
                try {
                    created = asCartOrder(
                        await SubscriptionService.checkoutCart({
                            seats,
                            billingFrequency,
                            expectedTotal: quotedDue,
                        }),
                    )
                } catch (err) {
                    const revised = quoteFromPriceChange(err)
                    const revisedVerdict = revised
                        ? serverPricingIsValid(revised, totalCents)
                        : null
                    if (!revisedVerdict?.ok) throw err
                    created = asCartOrder(
                        await SubscriptionService.checkoutCart({
                            seats,
                            billingFrequency,
                            expectedTotal: dollarsFromCents(revisedVerdict.total),
                        }),
                    )
                }
                if (cancelled) return
                const nextVerdict = serverPricingIsValid(created, totalCents)
                setOrder(created)
                setVerdict(nextVerdict)
                if (!nextVerdict.ok) {
                    setPhase('mismatch')
                    return
                }
                const clientSecret =
                    created?.ClientSecretId || created?.clientSecretId
                if (cancelled) return
                const publishableKey =
                    publishableFromApi.current ||
                    resolveStripePublishableKey(stripeKey || created)
                if (!clientSecret) {
                    throw new Error('Billing did not return a payment session.')
                }
                if (!publishableKey) {
                    throw new Error(
                        'Stripe publishable key is missing, so payment was not started.',
                    )
                }
                const stripe = await loadStripe(publishableKey)
                if (cancelled) return
                stripeRef.current = stripe
                elementsRef.current = stripe.elements({
                    clientSecret,
                    appearance: {
                        theme: 'stripe',
                        variables: {
                            colorPrimary: '#263BA6',
                            borderRadius: '10px',
                        },
                    },
                })
                rememberCheckout({
                    order: created,
                    seats,
                    frequency,
                    companyId,
                    publishableKey,
                    savePaymentMethod: Boolean(saveRef.current),
                })
                setPhase('pay')
            } catch (err) {
                if (cancelled) return
                setError(messageFrom(err))
                setPhase('error')
            }
        }
        start()
        return () => {
            cancelled = true
        }
    }, [stripeKey, companyId, seats, frequency, totalCents, attempt])

    useEffect(() => {
        if (phase !== 'pay' || !elementsRef.current) return undefined
        const host = document.getElementById('license-cart-payment')
        if (!host) return undefined
        host.innerHTML = ''
        const paymentElement = elementsRef.current.create('payment', {
            layout: {
                type: 'tabs',
                defaultCollapsed: false,
            },
            wallets: {
                applePay: 'auto',
                googlePay: 'auto',
                link: 'auto',
            },
        })
        paymentElement.mount('#license-cart-payment')
        return () => {
            try {
                paymentElement.unmount()
            } catch {
                host.innerHTML = ''
            }
        }
    }, [phase])

    const finishActivation = async intent => {
        await recordSuccessfulPayment(order, intent, companyId)
        const confirmation = await confirmLicenseOrder(order)
        clearPendingCheckout()
        onPaid({ order, confirmation, paymentIntent: intent })
    }

    const handlePay = async () => {
        if (paying || !order) return
        setPaying(true)
        setError('')
        try {
            if (paidIntent) {
                await finishActivation(paidIntent)
                return
            }
            if (!stripeRef.current || !elementsRef.current) return
            const { paymentIntent, error: stripeError } =
                await stripeRef.current.confirmPayment({
                    elements: elementsRef.current,
                    redirect: 'if_required',
                    confirmParams: {
                        return_url: `${window.location.origin}/app/account/checkout`,
                    },
                })
            if (stripeError) {
                setError(
                    stripeError.message ||
                        'Your card was declined. Premium was not activated, and your seats were not increased. You can try again.',
                )
                return
            }
            const status = paymentIntent?.status
            if (status !== 'succeeded' && status !== 'processing') {
                setError(
                    `Payment was not completed (${
                        status || 'unknown'
                    }). Premium was not activated.`,
                )
                return
            }
            try {
                await finishActivation(paymentIntent)
            } catch (err) {
                setPaidIntent(paymentIntent)
                setError(
                    `${messageFrom(
                        err,
                    )} The card payment succeeded, but Premium is not active yet. Use Finish activation. Do not submit the card again.`,
                )
            }
        } catch (err) {
            setError(messageFrom(err))
        } finally {
            setPaying(false)
        }
    }

    const chargedCents = verdict?.ok ? verdict.total : null
    const payLabel = (() => {
        if (paidIntent) return 'Finish activation'
        if (phase !== 'pay' || chargedCents == null) return 'Confirming price…'
        if (frequency === 'monthly') {
            return `Pay ${formatMoney(chargedCents)}/month`
        }
        return `Pay ${formatMoney(chargedCents)}`
    })()
    const renewal = formatLongDate(nextRenewalDate(frequency, order))
    const subtotalCents = verdict?.subtotal ?? totalCents
    const taxLabel = (() => {
        if (!verdict?.ok) return 'Calculated at checkout'
        if (verdict.tax == null) return 'None'
        return formatMoney(verdict.tax)
    })()

    return (
        <>
            <StepHead>
                <span className="num">3</span>
                Payment Information
            </StepHead>
            <PayStage>
            <div>
            <Secure>
                <Lock />
                <div>
                    <h2>Secure Payment</h2>
                    <p>
                        {formatNote ||
                            'Your payment information is encrypted and secure.'}
                    </p>
                </div>
            </Secure>
            <Rows>
                <div className="line">
                    <span>Product</span>
                    <strong>eAuditor Premium</strong>
                </div>
                <div className="line">
                    <span>Team members</span>
                    <strong>{seats}</strong>
                </div>
                <div className="line">
                    <span>Billing frequency</span>
                    <strong>{billingLabel}</strong>
                </div>
                <div className="line">
                    <span>Unit price</span>
                    <strong>
                        {frequency === 'annual'
                            ? formatMoney(unitCents)
                            : `${formatMoney(unitCents)} / month`}
                    </strong>
                </div>
                <div className="line">
                    <span>Subtotal</span>
                    <strong>{formatMoney(subtotalCents)}</strong>
                </div>
                {verdict?.discount > 0 && (
                    <div className="line">
                        <span>
                            {order?.DiscountLabel ||
                                order?.discountLabel ||
                                'Discount'}
                        </span>
                        <strong>-{formatMoney(verdict.discount)}</strong>
                    </div>
                )}
                <div className="line">
                    <span>Tax</span>
                    <strong>{taxLabel}</strong>
                </div>
                <div className="total" data-testid="license-charge">
                    <span>Total charged today</span>
                    <span>
                        {chargedCents == null
                            ? formatPerPeriod(totalCents, frequency)
                            : formatMoney(chargedCents)}
                    </span>
                </div>
            </Rows>
            {frequency === 'monthly' && chargedCents != null && (
                <Hint>
                    Recurring billing: {formatMoney(chargedCents)} each month
                    until you cancel. Next renewal {renewal}.
                </Hint>
            )}
            {frequency === 'annual' && (
                <Note className="annual">
                    <strong>Charged in full today</strong>
                    {order?.ChargeNote ||
                        order?.chargeNote ||
                        `The entire annual amount is collected at checkout. Expected renewal ${renewal}.`}
                </Note>
            )}
            </div>
            <div>
            {phase === 'prep' && (
                <Prep>
                    <div>Confirming this price with billing…</div>
                    <div className="spin" />
                </Prep>
            )}
            {phase === 'pay' && (
                <>
                    <PayHost id="license-cart-payment" />
                    <CheckRow>
                        <input
                            type="checkbox"
                            checked={saveCard}
                            onChange={event =>
                                onSaveCardChange(event.target.checked)
                            }
                        />
                        <span>
                            Save this payment method for future payments
                        </span>
                    </CheckRow>
                    <Legal>
                        By completing your purchase, you agree to our{' '}
                        <a href={TERMS_URL} target="_blank" rel="noreferrer">
                            Terms of Service
                        </a>{' '}
                        and{' '}
                        <a href={PRIVACY_URL} target="_blank" rel="noreferrer">
                            Privacy Policy
                        </a>
                        .
                    </Legal>
                </>
            )}
            {phase === 'mismatch' && (
                <ErrorBox>
                    Billing returned{' '}
                    {verdict?.total == null
                        ? 'no charge'
                        : formatMoney(verdict.total)}{' '}
                    for this order. The Premium price for {seats} team{' '}
                    {seats === 1 ? 'member' : 'members'} on {billingLabel} is{' '}
                    {formatPerPeriod(totalCents, frequency)}. Payment was not
                    started, so you have not been charged and Premium is not
                    active.
                </ErrorBox>
            )}
            {error && <ErrorBox>{error}</ErrorBox>}
            <ActionRow>
                <GhostButton type="button" onClick={onBack} disabled={paying}>
                    ← Back
                </GhostButton>
                {phase === 'mismatch' || phase === 'error' ? (
                    <PrimaryButton
                        type="button"
                        onClick={() => setAttempt(value => value + 1)}
                    >
                        Try again
                    </PrimaryButton>
                ) : (
                    <PrimaryButton
                        type="button"
                        onClick={handlePay}
                        disabled={
                            paying || phase !== 'pay' || chargedCents == null
                        }
                    >
                        {paying ? 'Processing…' : payLabel}
                    </PrimaryButton>
                )}
            </ActionRow>
            </div>
            </PayStage>
        </>
    )
}

export const LicenseShoppingCart = () => {
    const navigate = useNavigate()
    const { refetchUsage, usage } = useSubscriptionUsage()
    const activeMembers = Number(usage?.seats?.current)
    const activeFloor =
        Number.isInteger(activeMembers) && activeMembers > 0
            ? activeMembers
            : 0
    const [step, setStep] = useState(1)
    const [seats, setSeats] = useState(2)
    const [draft, setDraft] = useState(null)
    const [frequency, setFrequency] = useState('annual')
    const [saveCard, setSaveCard] = useState(true)
    const [plans, setPlans] = useState([])
    const [stripeKey, setStripeKey] = useState('')
    const [plansLoading, setPlansLoading] = useState(true)
    const [planError, setPlanError] = useState('')
    const [planAttempt, setPlanAttempt] = useState(0)
    const [reviewError, setReviewError] = useState('')
    const [receipt, setReceipt] = useState(null)
    const [activation, setActivation] = useState(null)
    const [activating, setActivating] = useState(false)
    const [returnWorking, setReturnWorking] = useState(() => {
        if (typeof window === 'undefined') return false
        return Boolean(
            new URLSearchParams(window.location.search).get('redirect_status'),
        )
    })
    const returnLock = useRef(false)
    const quote = quoteLicense(seats, frequency)
    // Stripe publishable key still comes from the plan catalogue.
    const plan = pickPremiumPlan(plans, frequency)
    const companyId = accountCompanyId()
    const shownSeats = draft === null ? String(seats) : draft
    const [teamMembers, setTeamMembers] = useState([])
    const [membersLoading, setMembersLoading] = useState(false)
    const [memberError, setMemberError] = useState('')
    const [deactivatingId, setDeactivatingId] = useState(null)

    const loadTeamMembers = useCallback(async () => {
        setMembersLoading(true)
        try {
            const result = await UsersService.getTeamUsers({
                PageNumber: 1,
                PageSize: 5000000,
            })
            const list = Array.isArray(result) ? result : result?.Items || []
            setTeamMembers(list)
            setMemberError('')
        } catch (err) {
            setMemberError(messageFrom(err))
        } finally {
            setMembersLoading(false)
        }
    }, [])

    useEffect(() => {
        loadTeamMembers()
    }, [loadTeamMembers])

    const activePeople = useMemo(() => {
        const currentEmail = String(AuthService.getUser()?.Email || '')
            .trim()
            .toLowerCase()
        return teamMembers.filter(member => {
            const email = String(member?.Email || '').trim().toLowerCase()
            if (currentEmail && email === currentEmail) return false
            return member?.IsActive !== false && member?.IsActive !== 0
        })
    }, [teamMembers])

    const deactivateMember = async member => {
        const uniqueId = Number(member?.unique_id ?? member?.UniqueId)
        if (!Number.isInteger(uniqueId) || uniqueId <= 0) {
            setMemberError('This team member has no numeric id, so they cannot be deactivated.')
            return
        }
        setDeactivatingId(uniqueId)
        setMemberError('')
        try {
            await UsersService.deactivateMember({ ...member, unique_id: uniqueId })
            await loadTeamMembers()
            await refetchUsage()
        } catch (err) {
            setMemberError(messageFrom(err))
        } finally {
            setDeactivatingId(null)
        }
    }

    useEffect(() => {
        let cancelled = false
        const load = async () => {
            setPlansLoading(true)
            try {
                const data = await SubscriptionService.getServicePlans()
                if (cancelled) return
                setPlans(planListFromResponse(data))
                setStripeKey(readStripeKey(data))
                setPlanError('')
            } catch (err) {
                if (cancelled) return
                setPlanError(messageFrom(err))
                setPlans([])
            } finally {
                if (!cancelled) setPlansLoading(false)
            }
        }
        load()
        return () => {
            cancelled = true
        }
    }, [planAttempt])

    useEffect(() => {
        const params = new URLSearchParams(window.location.search)
        const status = params.get('redirect_status')
        if (!status || returnLock.current) return undefined
        returnLock.current = true
        let mounted = true
        const resume = async () => {
            const pending = readPendingCheckout()
            if (pending?.seats) setSeats(pending.seats)
            if (
                pending?.frequency === 'annual' ||
                pending?.frequency === 'monthly'
            ) {
                setFrequency(pending.frequency)
            }
            if (pending?.savePaymentMethod != null) {
                setSaveCard(Boolean(pending.savePaymentMethod))
            }
            const fail = message => {
                if (!mounted) return
                setReviewError(message)
                setReturnWorking(false)
                setStep(pending?.order ? 2 : 1)
                window.history.replaceState(null, '', '/app/account/checkout')
            }
            if (!pending?.order) {
                fail(
                    'Checkout could not be restored. Premium was not activated. Review the cart before paying again.',
                )
                return
            }
            if (status !== 'succeeded' && status !== 'processing') {
                fail(
                    'Payment was not completed. Premium is not active, and your seats were not increased. Your selection is still here so you can try again.',
                )
                return
            }
            let paymentIntent = null
            try {
                const clientSecret = params.get('payment_intent_client_secret')
                if (clientSecret && pending.publishableKey) {
                    const stripe = await loadStripe(pending.publishableKey)
                    const retrieved = await stripe.retrievePaymentIntent(
                        clientSecret,
                    )
                    paymentIntent = retrieved?.paymentIntent || null
                }
                if (
                    paymentIntent &&
                    paymentIntent.status !== 'succeeded' &&
                    paymentIntent.status !== 'processing'
                ) {
                    fail(
                        'Payment was not completed. Premium is not active, and your seats were not increased. Your selection is still here so you can try again.',
                    )
                    return
                }
                await recordSuccessfulPayment(
                    pending.order,
                    paymentIntent,
                    pending.companyId,
                )
                const confirmation = await confirmLicenseOrder(pending.order)
                const savedOrder = await withServerOrder({
                    ...pending.order,
                    ...(confirmation && typeof confirmation === 'object'
                        ? confirmation
                        : {}),
                    CartOrderId: pending.order?.CartOrderId,
                    PaymentOrderId: pending.order?.PaymentOrderId,
                })
                if (!mounted) return
                clearPendingCheckout()
                setReceipt({
                    order: savedOrder,
                    seats: pending.seats,
                    frequency: pending.frequency,
                    email: accountEmail(),
                    paidAt: new Date().toISOString(),
                })
                setStep(4)
                setReturnWorking(false)
                window.history.replaceState(null, '', '/app/account/checkout')
                refetchUsage()
            } catch (err) {
                if (!mounted) return
                setActivation({
                    order: pending.order,
                    paymentIntent,
                    companyId: pending.companyId,
                    seats: pending.seats,
                    frequency: pending.frequency,
                    savePaymentMethod: pending.savePaymentMethod,
                    error: `${messageFrom(
                        err,
                    )} If the bank already took the payment, finish activation. Do not pay a second time.`,
                })
                setStep(3)
                setReturnWorking(false)
                window.history.replaceState(null, '', '/app/account/checkout')
            }
        }
        resume()
        return () => {
            mounted = false
        }
    }, [refetchUsage])

    const commitDraft = () => {
        if (draft === null) return
        const next = normalizeSeatCount(draft)
        if (next) setSeats(next)
        setDraft(null)
    }

    const onSeatChange = event => {
        const raw = event.target.value
        if (raw === '') {
            setDraft('')
            return
        }
        if (!/^\d+$/.test(raw)) return
        const next = Number(raw)
        if (next > MAX_SEATS) return
        if (next < MIN_SEATS) {
            setDraft(raw)
            return
        }
        setDraft(null)
        setSeats(next)
    }

    const changeSeats = delta => {
        setDraft(null)
        setSeats(current => {
            const next = current + delta
            if (next < MIN_SEATS || next > MAX_SEATS) return current
            return next
        })
    }

    const blockBadKeys = event => {
        if (['e', 'E', '+', '-', '.', ','].includes(event.key)) {
            event.preventDefault()
        }
    }

    const goToReview = () => {
        commitDraft()
        setReviewError('')
        setStep(2)
    }

    const goToPayment = () => {
        if (plansLoading) return
        setReviewError('')
        setActivation(null)
        setStep(3)
    }

    const handlePaid = async ({ order, confirmation }) => {
        const savedOrder = await withServerOrder({
            ...order,
            ...(confirmation && typeof confirmation === 'object'
                ? confirmation
                : {}),
            CartOrderId: order?.CartOrderId,
            PaymentOrderId: order?.PaymentOrderId,
        })
        setReceipt({
            order: savedOrder,
            seats: quote.seats,
            frequency: quote.frequency,
            email: accountEmail(),
            paidAt: new Date().toISOString(),
        })
        setActivation(null)
        setStep(4)
        refetchUsage()
    }

    const finishRedirectActivation = async () => {
        if (!activation || activating) return
        setActivating(true)
        try {
            await recordSuccessfulPayment(
                activation.order,
                activation.paymentIntent,
                activation.companyId,
            )
            const confirmation = await confirmLicenseOrder(activation.order)
            const savedOrder = await withServerOrder({
                ...activation.order,
                ...(confirmation && typeof confirmation === 'object'
                    ? confirmation
                    : {}),
                CartOrderId: activation.order?.CartOrderId,
                PaymentOrderId: activation.order?.PaymentOrderId,
            })
            clearPendingCheckout()
            setReceipt({
                order: savedOrder,
                seats: activation.seats,
                frequency: activation.frequency,
                email: accountEmail(),
                paidAt: new Date().toISOString(),
            })
            setActivation(null)
            setStep(4)
            refetchUsage()
        } catch (err) {
            setActivation(current => ({
                ...current,
                error: messageFrom(err),
            }))
        } finally {
            setActivating(false)
        }
    }

    const jump = target => {
        if (step === 4 || target >= step) return
        setActivation(null)
        setStep(target)
    }

    const receiptQuote = receipt
        ? quoteLicense(receipt.seats, receipt.frequency)
        : null
    const receiptVerdict = receiptQuote
        ? serverPricingIsValid(receipt.order, receiptQuote.totalCents)
        : null
    const receiptAmount =
        receiptVerdict?.ok && receiptVerdict.total != null
            ? receiptVerdict.total
            : receiptQuote?.totalCents
    const savingsLabel = formatMoney(ANNUAL_SAVINGS_PER_SEAT_CENTS)

    return (
        <Page className={step === 1 || step === 3 ? 'desk' : ''}>
            <Crumb>
                Settings › Account &amp; Plan › <strong>Checkout</strong>
            </Crumb>
            {returnWorking && (
                <Card>
                    <Prep>
                        <div>Confirming your payment…</div>
                        <div className="spin" />
                    </Prep>
                </Card>
            )}
            {!returnWorking && (
                <>
                    <Progress>
                        {STEPS.map(item => {
                            let className = ''
                            if (item.id === step) className = 'current'
                            else if (item.id < step && step !== 4) {
                                className = 'done can'
                            } else if (item.id < step) className = 'done'
                            return (
                                <li key={item.id}>
                                    <button
                                        type="button"
                                        className={className}
                                        onClick={() => jump(item.id)}
                                    >
                                        <span className="num">{item.id}</span>
                                        <span className="label">
                                            {item.label}
                                        </span>
                                    </button>
                                </li>
                            )
                        })}
                    </Progress>
                    {step === 1 && (
                        <PlanStage>
                            <Card>
                                <Title>Upgrade to eAuditor Premium</Title>
                                <Sub>
                                    Choose the number of team members and
                                    billing option.
                                </Sub>
                                <SeatRow>
                                    <FieldLabel>Team Members</FieldLabel>
                                    <Stepper>
                                        <button
                                            type="button"
                                            aria-label="Remove a team member"
                                            onClick={() => changeSeats(-1)}
                                            disabled={seats <= MIN_SEATS}
                                        >
                                            −
                                        </button>
                                        <input
                                            aria-label="Number of team members"
                                            inputMode="numeric"
                                            pattern="[0-9]*"
                                            value={shownSeats}
                                            onChange={onSeatChange}
                                            onBlur={commitDraft}
                                            onKeyDown={blockBadKeys}
                                        />
                                        <button
                                            type="button"
                                            aria-label="Add a team member"
                                            onClick={() => changeSeats(1)}
                                            disabled={seats >= MAX_SEATS}
                                        >
                                            +
                                        </button>
                                    </Stepper>
                                </SeatRow>
                                <Hint>
                                    Each team member gets full access to
                                    eAuditor Premium.
                                </Hint>
                                <FieldLabel>Billing Frequency</FieldLabel>
                                <FreqGrid
                                    role="radiogroup"
                                    aria-label="Billing frequency"
                                >
                                    <FreqCard
                                        type="button"
                                        role="radio"
                                        aria-checked={frequency === 'monthly'}
                                        className={
                                            frequency === 'monthly' ? 'on' : ''
                                        }
                                        onClick={() => setFrequency('monthly')}
                                    >
                                        <div className="top">
                                            <span className="dot" />
                                            <span className="name">
                                                Monthly
                                            </span>
                                            {frequency === 'monthly' && (
                                                <span
                                                    className="badge"
                                                    data-testid="license-total"
                                                >
                                                    Total{' '}
                                                    {formatMoney(
                                                        quote.totalCents,
                                                    )}{' '}
                                                    per month
                                                </span>
                                            )}
                                        </div>
                                        <div className="price">$12.99</div>
                                        <div className="per">
                                            per user / month
                                        </div>
                                    </FreqCard>
                                    <FreqCard
                                        type="button"
                                        role="radio"
                                        aria-checked={frequency === 'annual'}
                                        className={
                                            frequency === 'annual' ? 'on' : ''
                                        }
                                        onClick={() => setFrequency('annual')}
                                    >
                                        <div className="top">
                                            <span className="dot" />
                                            <span className="name">Annual</span>
                                            <span className="badge">
                                                Save {savingsLabel} per user
                                            </span>
                                        </div>
                                        <div className="price">$9.99</div>
                                        <div className="per">
                                            per user / month
                                        </div>
                                        <div className="fine">
                                            Billed $119.88 per user annually
                                        </div>
                                    </FreqCard>
                                </FreqGrid>
                                <AnnualNotice quote={quote} />
                                <Note className="warn">
                                    <strong>Need more seats?</strong>
                                    You can always add more team members later.
                                </Note>
                                {activeFloor > quote.seats && (
                                    <Note className="warn">
                                        <strong>
                                            {activeFloor} team members are
                                            already active
                                        </strong>
                                        Select at least {activeFloor} before
                                        payment, or deactivate someone below.
                                        Fewer seats are not accepted.
                                    </Note>
                                )}
                                {activeFloor > quote.seats && (
                                    <MemberBox>
                                        <strong>Active team members</strong>
                                        <p className="lead">
                                            Deactivate a member here to free a seat.
                                            You can stay on this page.
                                        </p>
                                        {memberError && <p className="err">{memberError}</p>}
                                        {membersLoading && activePeople.length === 0 && (
                                            <p className="lead">Loading team members…</p>
                                        )}
                                        <MemberList>
                                            {activePeople.map(member => {
                                                const memberId = member.unique_id ?? member.UniqueId
                                                return (
                                                    <MemberRow key={memberId}>
                                                        <div className="who">
                                                            <span className="name">{member.Name || member.Email}</span>
                                                            <span className="mail">{member.Email}</span>
                                                        </div>
                                                        <QuietButton
                                                            type="button"
                                                            disabled={deactivatingId != null}
                                                            onClick={() => deactivateMember(member)}
                                                        >
                                                            {deactivatingId === memberId ? 'Deactivating…' : 'Deactivate'}
                                                        </QuietButton>
                                                    </MemberRow>
                                                )
                                            })}
                                        </MemberList>
                                    </MemberBox>
                                )}
                                <PrimaryButton
                                    type="button"
                                    onClick={goToReview}
                                >
                                    Continue to Cart →
                                </PrimaryButton>
                            </Card>
                            <SideCart>
                                <h2>Your Cart</h2>
                                <p className="lead">
                                    Updates as you change seats or billing.
                                </p>
                                <Rows>
                                    {reviewRows(quote).map(([label, value]) => (
                                        <div className="line" key={label}>
                                            <span>{label}</span>
                                            <strong>{value}</strong>
                                        </div>
                                    ))}
                                    <div className="total">
                                        <span>
                                            {quote.frequency === 'annual'
                                                ? 'Due today'
                                                : 'Total'}
                                        </span>
                                        <span>
                                            {formatPerPeriod(
                                                quote.totalCents,
                                                quote.frequency,
                                            )}
                                        </span>
                                    </div>
                                </Rows>
                            </SideCart>
                        </PlanStage>
                    )}
                    {step !== 1 && (
                    <Card>
                        {step === 2 && (
                            <>
                                <StepHead>
                                    <span className="num">2</span>
                                    Review Your Cart
                                </StepHead>
                                <Title>Your Cart</Title>
                                <Sub>
                                    Review your selection before checkout.
                                </Sub>
                                <Product>
                                    <div className="mark">
                                        <Crown />
                                    </div>
                                    <div>
                                        <h2>eAuditor Premium</h2>
                                        <p>
                                            Professional tools for a safer, more
                                            efficient workplace.
                                        </p>
                                    </div>
                                </Product>
                                <Rows>
                                    {reviewRows(quote).map(([label, value]) => (
                                        <div className="line" key={label}>
                                            <span>{label}</span>
                                            <strong>{value}</strong>
                                        </div>
                                    ))}
                                    <div
                                        className="total"
                                        data-testid="license-total"
                                    >
                                        <span>
                                            {quote.frequency === 'annual'
                                                ? 'Amount due today'
                                                : 'Total'}
                                        </span>
                                        <span>
                                            {formatPerPeriod(
                                                quote.totalCents,
                                                quote.frequency,
                                            )}
                                        </span>
                                    </div>
                                </Rows>
                                <AnnualNotice quote={quote} />
                                {frequency === 'annual' && (
                                    <Note className="save">
                                        Save {savingsLabel} per user per year
                                        with annual billing. This does not
                                        change the amount due.
                                    </Note>
                                )}
                                {reviewError && (
                                    <ErrorBox>{reviewError}</ErrorBox>
                                )}
                                <ActionRow>
                                    <GhostButton
                                        type="button"
                                        onClick={() => setStep(1)}
                                    >
                                        ← Back
                                    </GhostButton>
                                    <PrimaryButton
                                        type="button"
                                        onClick={goToPayment}
                                        disabled={plansLoading}
                                    >
                                        {plansLoading
                                            ? 'Loading plan…'
                                            : 'Proceed to Checkout →'}
                                    </PrimaryButton>
                                </ActionRow>
                            </>
                        )}
                        {step === 3 && activation && (
                            <>
                                <StepHead>
                                    <span className="num">3</span>
                                    Payment Information
                                </StepHead>
                                <Title>Finish activation</Title>
                                <Sub>
                                    The bank step finished. Premium is not
                                    active until eAuditor confirms it.
                                </Sub>
                                {activation.error && (
                                    <ErrorBox>{activation.error}</ErrorBox>
                                )}
                                <ActionRow>
                                    <GhostButton
                                        type="button"
                                        onClick={() => {
                                            setActivation(null)
                                            setStep(2)
                                        }}
                                        disabled={activating}
                                    >
                                        ← Back
                                    </GhostButton>
                                    <PrimaryButton
                                        type="button"
                                        onClick={finishRedirectActivation}
                                        disabled={activating}
                                    >
                                        {activating
                                            ? 'Activating…'
                                            : 'Finish activation'}
                                    </PrimaryButton>
                                </ActionRow>
                            </>
                        )}
                        {step === 3 && !activation && (
                                <CartPayment
                                    stripeKey={
                                        plan?.StripeClientKey ||
                                        plan?.stripeClientKey ||
                                        stripeKey
                                    }
                                    companyId={companyId}
                                    quote={quote}
                                    saveCard={saveCard}
                                    onSaveCardChange={setSaveCard}
                                    onBack={() => setStep(2)}
                                    onPaid={handlePaid}
                                />
                            )}
                        {step === 4 && receiptQuote && (
                            <Success>
                                <div className="mark">
                                    <Check />
                                </div>
                                <h1>Welcome to eAuditor Premium!</h1>
                                <p className="lead">
                                    Your purchase was successful.
                                </p>
                                <Rows>
                                    <div className="line">
                                        <span>Team members</span>
                                        <strong>{receiptQuote.seats}</strong>
                                    </div>
                                    <div className="line">
                                        <span>Billing frequency</span>
                                        <strong>
                                            {receiptQuote.billingLabel}
                                        </strong>
                                    </div>
                                    <div className="line">
                                        <span>Amount</span>
                                        <strong>
                                            {formatPerPeriod(
                                                receiptAmount,
                                                receiptQuote.frequency,
                                            )}
                                        </strong>
                                    </div>
                                    <div className="line">
                                        <span>Order number</span>
                                        <strong>
                                            {orderNumberOf(receipt.order) ||
                                                'Recorded'}
                                        </strong>
                                    </div>
                                    <div className="line">
                                        <span>Date</span>
                                        <strong>
                                            {formatLongDate(receipt.paidAt)}
                                        </strong>
                                    </div>
                                    <div className="line">
                                        <span>Next renewal</span>
                                        <strong>
                                            {formatLongDate(
                                                nextRenewalDate(
                                                    receiptQuote.frequency,
                                                    receipt.order,
                                                ),
                                            )}
                                        </strong>
                                    </div>
                                </Rows>
                                {receiptQuote.frequency === 'annual' && (
                                    <Note className="annual">
                                        The full annual amount was charged with
                                        this payment.
                                    </Note>
                                )}
                                <Hint>
                                    {emailWasSent(receipt.order)
                                        ? 'A confirmation email has been sent to '
                                        : 'A confirmation email will be sent to '}
                                    <strong>
                                        {receipt.email ||
                                            'your account email address'}
                                    </strong>
                                    .
                                </Hint>
                                <PrimaryButton
                                    type="button"
                                    onClick={() => navigate(DASHBOARD_PATH)}
                                >
                                    Go to Dashboard
                                </PrimaryButton>
                                <SecondaryButton
                                    type="button"
                                    onClick={() => navigate(ACCOUNT_PLAN_PATH)}
                                >
                                    View Subscription Details
                                </SecondaryButton>
                            </Success>
                        )}
                        {step === 2 && planError && !plansLoading && (
                            <GhostButton
                                type="button"
                                onClick={() =>
                                    setPlanAttempt(value => value + 1)
                                }
                            >
                                Reload plans
                            </GhostButton>
                        )}
                    </Card>
                    )}
                </>
            )}
        </Page>
    )
}
